import React, { useState, useRef, useCallback } from 'react';

/**
 * PullToRefresh Component
 * 
 * Wraps content and provides pull-to-refresh functionality.
 * Designed for mobile touch devices.
 * 
 * @param {Object} props
 * @param {Function} props.onRefresh - Async function to call on refresh (should return a Promise)
 * @param {React.ReactNode} props.children - Content to wrap
 * @param {string} props.className - Additional classes for the container
 * @param {number} props.threshold - Pull distance to trigger refresh (default: 80)
 * @param {string} props.refreshingText - Text shown while refreshing
 * @param {string} props.pullText - Text shown while pulling
 * @param {string} props.releaseText - Text shown when threshold reached
 */
const PullToRefresh = ({
    onRefresh,
    children,
    className = '',
    threshold = 80,
    refreshingText = 'Refreshing...',
    pullText = 'Pull to refresh',
    releaseText = 'Release to refresh'
}) => {
    const [pullDistance, setPullDistance] = useState(0);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [showIndicator, setShowIndicator] = useState(false);

    const containerRef = useRef(null);
    const startY = useRef(0);
    const isPulling = useRef(false);

    const handleTouchStart = useCallback((e) => {
        // Only enable pull-to-refresh when scrolled to top
        const scrollTop = containerRef.current?.scrollTop || window.scrollY;
        if (scrollTop <= 0 && !isRefreshing) {
            startY.current = e.touches[0].clientY;
            isPulling.current = true;
        }
    }, [isRefreshing]);

    const handleTouchMove = useCallback((e) => {
        if (!isPulling.current || isRefreshing) return;

        const currentY = e.touches[0].clientY;
        const distance = currentY - startY.current;

        if (distance > 0) {
            // Apply resistance - the further you pull, the harder it gets
            const resistance = 0.4;
            const adjustedDistance = Math.min(distance * resistance, threshold * 1.5);
            setPullDistance(adjustedDistance);
            setShowIndicator(true);

            // Prevent default scrolling when pulling
            if (distance > 10) {
                e.preventDefault();
            }
        }
    }, [isRefreshing, threshold]);

    const handleTouchEnd = useCallback(async () => {
        if (!isPulling.current || isRefreshing) return;
        isPulling.current = false;

        if (pullDistance >= threshold && onRefresh) {
            setIsRefreshing(true);
            setPullDistance(threshold * 0.6); // Keep some distance while refreshing

            try {
                await onRefresh();
            } catch (error) {
                console.error('Refresh failed:', error);
            } finally {
                setIsRefreshing(false);
                setPullDistance(0);
                setShowIndicator(false);
            }
        } else {
            // Snap back
            setPullDistance(0);
            setTimeout(() => setShowIndicator(false), 200);
        }
    }, [pullDistance, threshold, onRefresh, isRefreshing]);

    const getIndicatorText = () => {
        if (isRefreshing) return refreshingText;
        if (pullDistance >= threshold) return releaseText;
        return pullText;
    };

    const indicatorProgress = Math.min(pullDistance / threshold, 1);

    return (
        <div
            ref={containerRef}
            className={`relative ${className}`}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
        >
            {/* Pull indicator */}
            {showIndicator && (
                <div
                    className="absolute left-0 right-0 flex flex-col items-center justify-center z-10 pointer-events-none transition-transform duration-200"
                    style={{
                        top: -60,
                        transform: `translateY(${pullDistance}px)`,
                    }}
                >
                    <div className="bg-[#141419] border border-white/10 rounded-full px-4 py-2 flex items-center gap-2 shadow-lg">
                        {/* Spinner */}
                        <div
                            className={`w-5 h-5 border-2 border-[#3E7BFA]/30 border-t-[#3E7BFA] rounded-full ${isRefreshing ? 'animate-spin' : ''}`}
                            style={{
                                transform: isRefreshing ? 'none' : `rotate(${indicatorProgress * 360}deg)`,
                                transition: 'transform 0.1s ease-out'
                            }}
                        />
                        {/* Text */}
                        <span className="text-sm text-gray-300 font-medium">
                            {getIndicatorText()}
                        </span>
                    </div>
                </div>
            )}

            {/* Content with pull offset */}
            <div
                style={{
                    transform: `translateY(${pullDistance}px)`,
                    transition: isPulling.current ? 'none' : 'transform 0.2s ease-out'
                }}
            >
                {children}
            </div>
        </div>
    );
};

export default PullToRefresh;
